import { FC } from 'react';
import { TextElement, TextProps, TextVariant } from './types';
import { cn } from '../../utils/cn';

/**
 * Default HTML elements for each text variant
 */
const defaultElements: Record<TextVariant, TextElement> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  'text-xl-bold': 'p',
  'text-lg-bold': 'p',
  'text-lg-medium': 'p',
  'text-lg-regular': 'p',
  'text-md-bold': 'p',
  'text-md-medium': 'p',
  'text-md-regular': 'p',
  'text-sm-medium': 'p',
  'text-sm-regular': 'p',
  'title-lg': 'h3',
  'title-md': 'h4',
  'body-lg-bold': 'p',
  'body-lg-regular': 'p',
} as const;

/**
 * Tailwind classes for each text variant
 */
const variantStyles: Record<TextVariant, string> = {
  // Display & Headings
  display: 'text-display font-bold tracking-tight',
  h1: 'text-h1 font-bold tracking-tight',
  h2: 'text-h2 font-bold tracking-tight',

  // Interface text
  'text-xl-bold': 'text-xl font-bold',
  'text-lg-bold': 'text-lg font-bold',
  'text-lg-medium': 'text-lg font-bold',
  'text-lg-regular': 'text-lg font-bold',
  'text-md-bold': 'text-md font-medium',
  'text-md-medium': 'text-md font-medium',
  'text-md-regular': 'text-md font-normal',
  'text-sm-medium': 'text-sm font-medium',
  'text-sm-regular': 'text-sm font-normal',

  // Body text
  'title-lg': 'text-title-lg font-bold tracking-tight',
  'title-md': 'text-title-md font-bold tracking-tight',
  'body-lg-bold': 'text-body-lg-bold font-bold',
  'body-lg-regular': 'text-body-lg font-normal',
} as const;

/**
 * Text component for consistent typography across the application
 */
export const Text: FC<TextProps> = ({ variant, as, className, children, ...props }) => {
  const Element = as || defaultElements[variant];

  return (
    <Element className={cn(variantStyles[variant], className)} {...props}>
      {children}
    </Element>
  );
};
